import { InfoTip } from '@/components/InfoTip';

// ── CapacityModeCard ──────────────────────────────────────────────────────────
// Rich, selectable mode tiles (coloured icon + title + feature bullets),
// replacing the plain radio list. Mirrors the salon dashboard's
// CapacityModeSelector, themed to the restaurant accent rather than per-mode
// hues. Presentational - the page owns the mode state and the switch logic.

export interface CapacityModeOption<T extends string> {
  value: T;
  /** F7 icon name shown in the tile. */
  icon: string;
  title: string;
  /** Short feature bullets describing the mode. */
  features: string[];
  /** Long-form copy for the info dialog. */
  details: string;
}

interface CapacityModeCardProps<T extends string> {
  options: CapacityModeOption<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Section header (localized by the caller). */
  heading: string;
  /** Optional muted note shown directly under the header (localized by the caller). */
  note?: string;
  /** Builds the info-button aria-label for a given mode title (localized). */
  infoAriaLabel: (title: string) => string;
}

export function CapacityModeCard<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  heading,
  note,
  infoAriaLabel,
}: CapacityModeCardProps<T>) {
  return (
    <div className="capacity-mode-section">
      <div className="setting-card__header">
        <span className="setting-card__icon">
          <i className="f7-icons">slider_horizontal_3</i>
        </span>
        <span className="setting-card__title">{heading}</span>
      </div>
      {note && (
        <div
          style={{
            color: 'var(--f7-block-footer-text-color)',
            fontSize: 14,
            marginTop: -6,
            padding: '0 16px 10px',
          }}
        >
          {note}
        </div>
      )}

      <div className="capacity-modes">
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            // A div (not a button) so the nested info <a> stays valid markup.
            <div
              key={opt.value}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-pressed={active}
              className={[
                'capacity-mode-tile',
                active ? 'capacity-mode-tile--active' : '',
                disabled ? 'capacity-mode-tile--disabled' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => !disabled && !active && onChange(opt.value)}
              onKeyDown={(e) => {
                if (disabled || active) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChange(opt.value);
                }
              }}
            >
              <div className="capacity-mode-tile__head">
                <span className="capacity-mode-tile__icon">
                  <i className="f7-icons">{opt.icon}</i>
                </span>
                <span className="capacity-mode-tile__title">{opt.title}</span>
                <InfoTip text={opt.details} aria-label={infoAriaLabel(opt.title)} />
                <i
                  className="f7-icons capacity-mode-tile__check"
                  style={{
                    color: active ? 'var(--rt-accent)' : 'var(--f7-list-item-subtitle-text-color)',
                  }}
                >
                  {active ? 'checkmark_circle_fill' : 'circle'}
                </i>
              </div>
              <ul className="capacity-mode-tile__features">
                {opt.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
